import Item from './Item';
import ProjectPart from './ProjectPart';
import * as AT from '../../components/container/attributes';
import {HitUtils} from '@hit/components';

export default class PositionItem extends Item {
  constructor({
    id,
    number,
    designation,
    description = undefined,
    parent = undefined,
    active = true,
    option = undefined,
    itemType = undefined,
    titleLevel = undefined,
    resource = undefined,
    adminType = undefined,
    quantity = undefined,
    unit = undefined,
    discount = undefined,
    taxCode = undefined,
    comment = undefined,
    calculated = false,
    projectPart = undefined,
    netPrice = undefined,
    netAmount = undefined,
    grossPrice = undefined,
    grossAmount = undefined,
    salesPrice = undefined,
    salesAmount = undefined,
    scriptParam = undefined,
    designation_template = undefined,
    description_template = undefined,
    overhead = undefined,
    netOverhead = undefined,
    grossOverhead = undefined,
    parentItemId = undefined,
    groupProjectPartItemId = undefined,
    pricePrecision = undefined,
    customData = undefined,
    tags = [],
    externalReference = undefined,
    // We also need to add the key how it is stored in the db (we use the db column names)
    no = undefined,
  }) {
    super({
      id,
      number: number || no,
      designation,
      description,
      parent,
      active,
      option,
      itemType,
      titleLevel,
      resource,
      adminType,
      quantity,
      unit,
      netPrice,
      netAmount,
      grossPrice,
      grossAmount,
      salesPrice,
      salesAmount,
      discount,
      taxCode,
      comment,
      calculated,
    });
    this.projectPart = projectPart ? new ProjectPart(projectPart) : null;
    this.netPrice = netPrice;
    this.netAmount = netAmount;
    this.grossPrice = grossPrice;
    this.grossAmount = grossAmount;
    this.salesPrice = salesPrice;
    this.salesAmount = salesAmount;
    this.scriptParam = scriptParam;
    this.designation_template = designation_template;
    this.description_template = description_template;
    this.overhead = overhead;
    this.netOverhead = netOverhead;
    this.grossOverhead = grossOverhead;
    this.parentItemId = parentItemId;
    this.groupProjectPartItemId = groupProjectPartItemId;
    this.pricePrecision = pricePrecision;
    this.customData = customData;
    this.tags = tags;
    this.externalReference = externalReference;
  }

  get fullDesignation() {
    return HitUtils.createStandardDesignation({
      number: this.number,
      designation: this.designation,
    });
  }
  static get orderKey() {
    return 'sort_order';
  }
  static get fkName() {
    return 'project_part_item_id';
  }
  static get apiRoute() {
    return 'project_part_item';
  }
  static get detailPageRoute() {
    return 'positionDetail';
  }
  static get listPageRoute() {
    return 'positionDetail';
  }
  static get joinAttrsKey() {
    return ['number', 'designation', 'quantity', 'projectPart', 'scriptParam'];
  }
  static get browserDisplayAttrs() {
    return this.getAttributes(
      ['number', 'designation', 'unit', 'quantity'],
      ['sortOrder'],
    );
  }
  get fkName() {
    return PositionItem.fkName;
  }
  get apiRoute() {
    return PositionItem.apiRoute;
  }
  get listPageRoute() {
    return PositionItem.listPageRoute;
  }
  get detailPageRoute() {
    return PositionItem.detailPageRoute;
  }
  static get entityListDisplayAttrs() {
    return this.getAttributes(
      ['number', 'designation', 'unit', 'quantity'],
      [
        'netPrice',
        'netAmount',
        'grossPrice',
        'grossAmount',
        'salesPrice',
        'salesAmount',
      ],
    );
  }
  static get listDisplayAttrs() {
    return this.getAttributes(
      [
        'resource',
        'itemType',
        'number',
        'designation',
        'unit',
        'adminType',
        'quantity',
        'netPrice',
        'netAmount',
        'netOverhead',
        'grossPrice',
        'discount',
        'grossOverhead',
        'salesPrice',
        'salesAmount',
        'taxCode',
        'comment',
        'externalReference',
        'option',
        'overhead',
        'index',
        'tags',
      ],
      [
        'titleLevel',
        'description',
        'calculated',
        'sortOrder',
        'projectPart',
        'scriptParam',
        'designation_template',
        'description_template',
        'totalPeriodQuantity',
        'active',
        'parentItemId',
        'groupProjectPartItemId',
        'pricePrecision',
        'customData',
      ],
    );
  }

  static get allAttributes() {
    const baseAttributes = Item.allAttributes;
    const unitAttr = baseAttributes.find((attr) => attr.name === 'unit');
    unitAttr.joinPath = ['unit!fk_project_part_item_unit_id_unit'];
    const taxCodeAttr = baseAttributes.find((attr) => attr.name === 'taxCode');
    taxCodeAttr.joinPath = [
      'tax_code!fk_project_part_item_tax_code_id_tax_code',
    ];
    taxCodeAttr.filters = {
      journal_type: 'eq.SALE',
    };
    const resourceAttr = baseAttributes.find(
      (attr) => attr.name === 'resource',
    );
    resourceAttr.joinPath = [
      'resource!fk_project_part_item_resource_id_resource',
    ];
    resourceAttr.tableProperties = {
      ...resourceAttr.tableProperties,
      header: 'ONLY_SHOW_IN_EDITION',
      onlyShowInEdition: true,
      hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM', 'ITEM_GROUP'],
    };
    const customAttributes = [
      new AT.FkTransactionAttribute({
        name: 'transaction',
        joinPath: [
          'transaction!fk_transaction_item_transaction_id_transaction',
        ],
        tableProperties: {
          header: 'hit-base.common.transaction',
        },
      }),
      new AT.FkProjectPartAttribute({
        name: 'projectPart',
        joinPath: [
          'project_part!fk_project_part_item_project_part_id_project_part',
        ],
        fkToUpdate: 'project_part_id',
      }),
      new AT.DecimalAttribute({
        name: 'netPrice',
        column: 'net_price',
        creationValue: 0.0,
        decimalPrecisionForcedByColumn: 'pricePrecision',
        tableProperties: {
          header: 'hit-app.common.net-price-short',
          maxWidth: '0.6fr',
          hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM_GROUP'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'netAmount',
        decimalPrecision: 2,
        column: 'net_amount',
        readOnly: true,
        default: 0.0,
        tableProperties: {
          header: 'hit-app.common.net-amount-short',
          maxWidth: '0.65fr',
          readOnly: true,
          hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM_GROUP'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'netOverhead',
        column: 'net_overhead',
        creationValue: 0.0,
        decimalPrecisionForcedByColumn: 'pricePrecision',
        readOnly: true,
        tableProperties: {
          header: 'hit-app.project-part-item.net-overhead',
          maxWidth: '0.6fr',
          readOnly: true,
          hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM_GROUP'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'grossPrice',
        decimalPrecisionForcedByColumn: 'pricePrecision',
        column: 'gross_price',
        default: 0.0,
        tableProperties: {
          header: 'hit-app.common.gross-price-short',
          maxWidth: '0.6fr',
          hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM_GROUP'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'grossOverhead',
        column: 'gross_overhead',
        decimalPrecisionForcedByColumn: 'pricePrecision',
        creationValue: 0.0,
        readOnly: true,
        tableProperties: {
          header: 'hit-app.project-part-item.gross-overhead',
          maxWidth: '0.6fr',
          readOnly: true,
          hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM_GROUP'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'grossAmount',
        decimalPrecision: 2,
        column: 'gross_amount',
        default: 0.0,
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.gross-amount',
          maxWidth: '0.65fr',
          hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM_GROUP'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'salesPrice',
        decimalPrecisionForcedByColumn: 'pricePrecision',
        column: 'sales_price',
        default: 0.0,
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.sales-price-short',
          maxWidth: '0.6fr',
          readOnly: true,
          hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM_GROUP'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'salesAmount',
        decimalPrecision: 2,
        column: 'sales_amount',
        default: 0.0,
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.sales-amount-short',
          maxWidth: '0.65fr',
          readOnly: true,
          hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM_GROUP'],
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'scriptParam',
        column: 'script_param',
        default: {},
      }),
      new AT.JsonObjectAttribute({
        name: 'customData',
        column: 'custom_data',
      }),
      new AT.FkProjectPartIndexAttribute({
        name: 'index',
        column: 'project_part_index_id',
        joinPath: [
          'project_part_index!fk_project_part_item_project_part_index_id_project_part_index',
        ],
        tableProperties: {
          header: 'hit-app.common.index',
          maxWidth: '0.5fr',
          hideInEditionForItemTypes: ['COMMENT', 'TITLE', 'ITEM_GROUP'],
        },
      }),
      new AT.DecimalAttribute({
        readOnly: true,
        name: 'totalPeriodQuantity',
        column: 'project_part_bill_quantity(quantity.sum())',
        decimalPrecision: 2,
        decimalDefaultValue: 0,
        transform: (data) => data?.[0]?.sum ?? 0,
      }),
      new AT.StringAttribute({
        name: 'designation_template',
      }),
      new AT.RichTextAttribute({
        name: 'description_template',
      }),
      new AT.BooleanAttribute({
        name: 'overhead',
      }),
      new AT.StringAttribute({
        name: 'parentItemId',
        column: 'parent_project_part_item_id',
      }),
      new AT.StringAttribute({
        name: 'groupProjectPartItemId',
        column: 'group_project_part_item_id',
      }),
      new AT.IntegerAttribute({
        name: 'pricePrecision',
        column: 'price_precision',
        tableProperties: {
          header: 'hit-app.common.price-precision',
          maxWidth: '0.5fr',
        },
      }),
      new AT.StringAttribute({
        name: 'externalReference',
        column: 'external_reference',
        tableProperties: {
          header: 'hit-app.project-part-item.external-reference',
          maxWidth: '1fr',
          hideByDefault: true,
        },
      }),
      this.TAGS_ATTRIBUTE(
        {moduleUsage: 'project', tableOrigin: 'project_part_item'},
        {hideByDefault: true},
      ),
    ];
    return baseAttributes.concat(customAttributes);
  }
}
